import { format } from 'date-fns';

const DATE_FORMAT = 'yyyy-MM-dd';

export class AnswerChangeDetector {
  static simple(
    previousValue: unknown,
    currentValue: unknown,
    includeRemoval = true,
  ): boolean {
    const previous = this.normalizeSimpleValue(previousValue);
    const current = this.normalizeSimpleValue(currentValue);

    if (!current) {
      return includeRemoval && !!previous;
    }

    return !previous || current !== previous;
  }

  static field(
    previousValue: unknown,
    field: string,
    currentValue: unknown,
    includeRemoval = true,
  ): boolean {
    return this.simple(
      this.readJsonField(previousValue, field),
      currentValue,
      includeRemoval,
    );
  }

  static list<T>(
    previousValue: unknown,
    currentValues: T[],
    extractPreviousValues: (parsed: unknown) => T[],
  ): boolean {
    if (!previousValue) {
      return currentValues.length > 0;
    }

    try {
      const previousValues = extractPreviousValues(
        JSON.parse(String(previousValue)),
      );
      return (
        JSON.stringify([...currentValues].sort()) !==
        JSON.stringify([...previousValues].sort())
      );
    } catch {
      return false;
    }
  }

  static date(previousValue: unknown, currentValue: unknown): boolean {
    return this.simple(
      this.normalizeDateValue(previousValue),
      this.normalizeDateValue(currentValue),
      true,
    );
  }

  static dateField(
    previousValue: unknown,
    field: 'fromDate' | 'toDate',
    currentValue: unknown,
  ): boolean {
    return this.simple(
      this.normalizeDateValue(this.readJsonField(previousValue, field)),
      this.normalizeDateValue(currentValue),
      true,
    );
  }

  static checkboxIds(
    previousValue: unknown,
    currentValue: string | undefined,
  ): string[] {
    const currentIds = this.toIdArray(currentValue);
    const previousIds = this.toIdArray(previousValue);
    return currentIds.filter((id) => !previousIds.includes(id));
  }

  static addedIds<T>(
    previousValue: unknown,
    currentItems: T[],
    extractId: (item: T) => string | undefined,
    extractPreviousItems: (parsed: unknown) => T[] = (parsed) =>
      (parsed as T[]) ?? [],
  ): string[] {
    const currentIds = (currentItems ?? [])
      .map((item) => extractId(item))
      .filter((id): id is string => id !== undefined);

    if (!previousValue) {
      return currentIds;
    }

    try {
      const previousItems = extractPreviousItems(
        JSON.parse(String(previousValue)),
      );
      const previousIds = (previousItems ?? [])
        .map((item) => extractId(item))
        .filter((id): id is string => id !== undefined);
      return currentIds.filter((id) => !previousIds.includes(id));
    } catch {
      return [];
    }
  }

  static personField(
    previousValue: unknown,
    personIndex: number,
    field: string,
    currentValue: unknown,
  ): boolean {
    if (personIndex < 0) {
      return false;
    }

    const parsedPrevious = this.parseJson(previousValue);
    const previousPerson = Array.isArray(parsedPrevious)
      ? (parsedPrevious[personIndex] as Record<string, unknown> | undefined)
      : undefined;
    return this.simple(previousPerson?.[field], currentValue);
  }

  private static toIdArray(value: unknown): string[] {
    if (!value) {
      return [];
    }

    try {
      const parsed = JSON.parse(String(value));
      return Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
    } catch {
      return [String(value)];
    }
  }

  private static readJsonField(value: unknown, field: string): unknown {
    const parsed = this.parseJson(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)[field]
      : undefined;
  }

  private static parseJson(value: unknown): unknown {
    if (!value) {
      return undefined;
    }

    try {
      return JSON.parse(String(value));
    } catch {
      return undefined;
    }
  }

  static normalizeSimpleValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    try {
      const parsed = JSON.parse(String(value));
      if (
        Array.isArray(parsed) &&
        parsed.every(
          (item) => typeof item === 'string' || typeof item === 'number',
        )
      ) {
        return [...parsed].map(String).sort().join(',');
      }
      if (
        typeof parsed === 'string' ||
        typeof parsed === 'number' ||
        typeof parsed === 'boolean'
      ) {
        return String(parsed);
      }
    } catch {
      return String(value);
    }

    return String(value);
  }

  private static normalizeDateValue(value: unknown): string {
    if (!value) {
      return '';
    }

    if (value instanceof Date) {
      return format(value, DATE_FORMAT);
    }

    const valueAsString = String(value).trim();
    if (!valueAsString || /^\d{4}-\d{2}-\d{2}$/.test(valueAsString)) {
      return valueAsString;
    }

    const parsedDate = new Date(valueAsString);
    return Number.isNaN(parsedDate.getTime())
      ? valueAsString
      : format(parsedDate, DATE_FORMAT);
  }
}
